import {
  ChevronRight,
  Users,
  Briefcase,
  Car,
  Coffee,
  Clock,
  Shield,
  Headphones,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import RelatedServices from '../components/RelatedServices';
import Seo from '../components/Seo';
import { ServiceSchema } from '../components/SchemaMarkup';

function Services() {
  const breadcrumbs = [
    { label: 'Services', path: '/services' },
  ];

  return (
    <div className="font-sans text-black">
      <Seo
        title="Airport Services | Meet & Greet, Lounge, Chauffeur, Baggage"
        description="Explore Moreflex Aviation's premium airport services: meet & greet, lounge access, chauffeur & fleet, baggage assistance, fast-track security, and concierge support."
        path="/services"
      />
      <ServiceSchema
        name="Aviation Support Services"
        description="Premium airport services including meet & greet, lounge access, chauffeur, and baggage assistance"
        url="https://www.moreflexaviation.com/services"
        areaServed={['Kenya', 'Africa', 'Europe', 'Middle East', 'Worldwide']}
        serviceType="VIP Airport Services"
      />
      <Header />
      <Breadcrumb items={breadcrumbs} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-[#0b1f33] to-[#1a3c55] text-white py-20 overflow-hidden">
        <div className="hero-pattern absolute inset-0" />
        <div className="container relative mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="eyebrow justify-center mb-4 animate-fadeInTop">What We Do</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeInTop">
              Our Services
            </h1>
            <p className="text-lg text-white/75 mb-8 animate-fadeIn">
              Discover our comprehensive range of premium aviation support
              services designed to make your travel experience seamless and
              comfortable.
            </p>
            <div className="flex justify-center gap-4 animate-fadeInBottom">
              <Link to="/contact" className="btn-primary">
                Book Now <ChevronRight size={18} />
              </Link>
              <Link to="/contact" className="btn-outline-light">
                Contact Us <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-[#73BDF2] rounded-full opacity-10 transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#73BDF2] rounded-full opacity-10 transform translate-x-1/4 translate-y-1/4"></div>
      </section>

      {/* Main Services Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="eyebrow justify-center mb-3">Our Premium Services</p>
            <h2 className="section-title mb-6">
              Comprehensive Aviation Support Solutions
            </h2>
            <p className="max-w-3xl mx-auto text-gray-600">
              At Moreflex Aviation, we offer a wide range of premium services
              designed to enhance your travel experience. From personalized meet
              and greet to luxury lounges and transportation, we've got you
              covered. Explore our offerings below or{' '}
              <Link to="/contact" className="text-[#73BDF2] hover:text-[#5ba8e0]">
                contact our team
              </Link>
              {' '}for custom packages.
            </p>
          </div>

          {/* Service 1 - Meet and Greet */}
          <div id="meet-greet" className="flex flex-col md:flex-row items-center mb-20 animate-fadeIn scroll-mt-20">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-12">
              <div className="bg-gradient-to-r from-[#73BDF2] to-[#000000] p-1 rounded-2xl shadow-elevated">
                <div className="aspect-[4/3] rounded-[14px] overflow-hidden bg-black/20">
                  <img
                  src="https://images.unsplash.com/photo-1530521954074-e64f6810b32d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                  alt="Meet and Greet Service"
                  className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="flex items-center mb-4">
                <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4">
                  <Users className="text-[#73BDF2]" size={24} />
                </div>
                <h3 className="text-2xl font-bold">Meet and Greet Service</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Our Meet and Greet service provides a personalized welcome and
                assistance throughout your airport journey. From the moment you
                arrive at the airport until you board your flight, our
                professional staff will be by your side, ensuring a smooth and
                stress-free experience.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Personalized welcome at airport arrival or departure</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Assistance with check-in and boarding procedures</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>
                    Fast-track through security and immigration where available
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Escort to departure gate or arrival transportation</p>
                </div>
              </div>
              <button className="btn-primary">
                Book This Service <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Service 2 - Lounge Services */}
          <div id="lounge" className="flex flex-col md:flex-row-reverse items-center mb-20 animate-fadeIn scroll-mt-20">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pl-12">
              <div className="bg-gradient-to-r from-[#000000] to-[#73BDF2] p-1 rounded-2xl shadow-elevated">
                <div className="aspect-[4/3] rounded-[14px] overflow-hidden bg-black/20">
                  <img
                  src="https://images.unsplash.com/photo-1572177215152-32f247303126?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                  alt="Lounge Services"
                  className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="flex items-center mb-4">
                <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4">
                  <Coffee className="text-[#73BDF2]" size={24} />
                </div>
                <h3 className="text-2xl font-bold">Lounge Services</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Escape the hustle and bustle of the airport terminal with our
                exclusive lounge access services. Relax in comfort, enjoy
                complimentary refreshments, and prepare for your journey in a
                peaceful environment. Our lounge services are available at major
                airports worldwide. Learn more about our{' '}
                <Link to="/partners" className="text-[#73BDF2] hover:text-[#5ba8e0]">
                  partner airports
                </Link>
                .
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>
                    Access to premium airport lounges regardless of ticket class
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Complimentary food and beverages</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Wi-Fi, business facilities, and entertainment options</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Shower facilities at select locations</p>
                </div>
              </div>
              <Link to="/contact" className="btn-primary">
                Book This Service
              </Link>
            </div>
          </div>

          {/* Service 3 - Chauffeur & Fleet Services */}
          <div id="chauffeur" className="flex flex-col md:flex-row items-center mb-20 animate-fadeIn scroll-mt-20">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-12">
              <div className="bg-gradient-to-r from-[#73BDF2] to-[#000000] p-1 rounded-2xl shadow-elevated">
                <div className="aspect-[4/3] rounded-[14px] overflow-hidden bg-black/20">
                  <img
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                  alt="Chauffeur & Fleet Services"
                  className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="flex items-center mb-4">
                <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4">
                  <Car className="text-[#73BDF2]" size={24} />
                </div>
                <h3 className="text-2xl font-bold">
                  Chauffeur & Fleet Services
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Travel in style and comfort with our premium chauffeur and fleet
                services. Our professional drivers will ensure you reach your
                destination safely and on time. We offer a range of luxury
                vehicles to suit your needs, from executive sedans to spacious
                SUVs. Check out our{' '}
                <Link to="/about" className="text-[#73BDF2] hover:text-[#5ba8e0]">
                  about page
                </Link>
                {' '}to learn more about our commitment to excellence.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Professional, experienced, and multilingual drivers</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Luxury vehicle fleet including sedans, SUVs, and vans</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Airport transfers and city transportation</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>24/7 availability with real-time flight monitoring</p>
                </div>
              </div>
              <Link to="/contact" className="btn-primary">
                Book This Service
              </Link>
            </div>
          </div>

          {/* Service 4 - Baggage Assistance */}
          <div id="baggage" className="flex flex-col md:flex-row-reverse items-center animate-fadeIn scroll-mt-20">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pl-12">
              <div className="bg-gradient-to-r from-[#000000] to-[#73BDF2] p-1 rounded-2xl shadow-elevated">
                <div className="aspect-[4/3] rounded-[14px] overflow-hidden bg-black/20">
                  <img
                  src="https://images.unsplash.com/photo-1708403120467-1715bb6840df?q=80&w=1587&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="Baggage Assistance"
                  className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="flex items-center mb-4">
                <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4">
                  <Briefcase className="text-[#73BDF2]" size={24} />
                </div>
                <h3 className="text-2xl font-bold">Baggage Assistance</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Let us handle the hassle of managing your luggage. Our baggage
                assistance service ensures your belongings are safely
                transported and handled with care. From check-in to collection,
                we'll take care of everything, allowing you to enjoy a
                stress-free journey.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Assistance with check-in and baggage drop-off</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Priority baggage handling and collection</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Special handling for fragile or valuable items</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/10 p-1 rounded-full mr-3 mt-1">
                    <ChevronRight size={16} className="text-[#73BDF2]" />
                  </div>
                  <p>Assistance with lost or delayed baggage</p>
                </div>
              </div>
              <Link to="/contact" className="btn-primary">
                Book This Service
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="eyebrow justify-center mb-3">Additional Offerings</p>
            <h2 className="section-title mb-6">Specialized Services</h2>
            <p className="max-w-3xl mx-auto text-gray-600">
              Beyond our core services, we offer specialized assistance tailored
              to specific needs and situations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Additional Service 1 */}
            <div id="fast-track" className="card-surface overflow-hidden transform hover:-translate-y-2 transition-transform duration-300 group scroll-mt-20">
              <div className="livery-stripe" />
              <div className="p-6">
                <div className="bg-[#73BDF2]/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#73BDF2]/20 transition-colors duration-300">
                  <Clock size={32} className="text-[#73BDF2]" />
                </div>
                <h3 className="text-xl font-bold mb-4">Fast Track Services</h3>
                <p className="text-gray-600 mb-6">
                  Skip the lines with our fast track services through security,
                  immigration, and customs. Save time and reduce stress during
                  your journey.
                </p>
                <Link
                  to="/contact"
                  className="text-[#73BDF2] font-medium flex items-center group-hover:underline"
                >
                  Learn more <ChevronRight size={16} className="ml-1" />
                </Link>
              </div>
            </div>

            {/* Additional Service 2 */}
            <div className="card-surface overflow-hidden transform hover:-translate-y-2 transition-transform duration-300 group">
              <div className="livery-stripe" />
              <div className="p-6">
                <div className="bg-[#73BDF2]/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#73BDF2]/20 transition-colors duration-300">
                  <Shield size={32} className="text-[#73BDF2]" />
                </div>
                <h3 className="text-xl font-bold mb-4">
                  VIP Security Services
                </h3>
                <p className="text-gray-600 mb-6">
                  Enhanced security measures for high-profile travelers.
                  Discreet and professional protection throughout your journey.
                </p>
                <Link
                  to="/contact"
                  className="text-[#73BDF2] font-medium flex items-center group-hover:underline"
                >
                  Learn more <ChevronRight size={16} className="ml-1" />
                </Link>
              </div>
            </div>

            {/* Additional Service 3 */}
            <div id="concierge" className="card-surface overflow-hidden transform hover:-translate-y-2 transition-transform duration-300 group scroll-mt-20">
              <div className="livery-stripe" />
              <div className="p-6">
                <div className="bg-[#73BDF2]/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#73BDF2]/20 transition-colors duration-300">
                  <Headphones size={32} className="text-[#73BDF2]" />
                </div>
                <h3 className="text-xl font-bold mb-4">Concierge Services</h3>
                <p className="text-gray-600 mb-6">
                  Personalized assistance with hotel bookings, restaurant
                  reservations, event tickets, and other travel arrangements.
                </p>
                <Link
                  to="/contact"
                  className="text-[#73BDF2] font-medium flex items-center group-hover:underline"
                >
                  Learn more <ChevronRight size={16} className="ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <RelatedServices currentService="services" />

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-black via-[#0b1f33] to-[#1a3c55] text-white overflow-hidden">
        <div className="hero-pattern absolute inset-0" />
        <div className="container relative mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-fadeInTop">
              Ready to Experience Premium Airport Services?
            </h2>
            <p className="text-lg text-white/75 mb-8 animate-fadeIn">
              Contact us today to learn more about how we can enhance your
              travel experience with our premium services or check out our{' '}
              <Link to="/partners" className="text-[#73BDF2] hover:text-white underline">
                partners
              </Link>
              {' '}and affiliations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 animate-fadeInBottom">
              <Link to="/contact" className="btn-primary justify-center">
                Book Our Services <ChevronRight size={18} />
              </Link>
              <Link to="/contact" className="btn-outline-light justify-center">
                Contact Us <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Services;
